// Núcleo dos insights (P-27; desenho da FA-04, §9): o que o evento `insight_state` (events.ts) e o validador dos eventos
// (validate.ts) compartilham com o índice e com o sinal (insights.ts): o lugar do cartão, a chave dele e a máquina de
// estados. Módulo folha, de propósito: events.ts e validate.ts estão no caminho crítico do site ao vivo, e insights.ts
// traz os schemas do pacote de evidências e do acervo (→ sections.ts). Importando daqui, o evento não os leva para o JS
// crítico (B01, §15.7). Só importa base.ts, que o caminho crítico já carrega. Os schemas zod que o evento não usa
// (quesito, nível, transição, desfecho, retirada) moram em insights.ts: schema no topo do módulo não sai na poda de
// código morto. insights.ts reexporta tudo daqui; quem não está no caminho crítico importa de lá.
import * as z from 'zod/mini';
import { IntSchema, type Iso, PLACE_CODES, type PlaceCode, PlaceCodeSchema, REGION_CODES } from './base';

/**
 * O quesito do sinal, na ordem fixa em que a tela lista os sinais de um cartão. `tally_timing` e `transfer` nunca saem
 * ao vivo (DR-33). share = fatia do candidato entre os válidos; blank_null = brancos e nulos somados.
 */
export type InsightMetric =
  | 'share'
  | 'turnout'
  | 'blank_null'
  | 'blank'
  | 'null'
  | 'profile'
  | 'tally_timing'
  | 'transfer';
/** Os dois níveis públicos (DR-33): "Sinal preliminar" (lugar ainda parcial) e "Alerta" (lugar completo). */
export type InsightLevel = 'preliminary' | 'alert';
/**
 * preliminary = sinal preliminar aberto; confirmed = o preliminar virou alerta; dissipated = os números se normalizaram
 * com o restante da apuração; alert = entrada direta, com o lugar já completo, sem sinal preliminar antes.
 */
export type InsightState = 'preliminary' | 'confirmed' | 'dissipated' | 'alert';
/** Do mais agregado ao mais fino. Ao vivo, só MUN e ABROAD_CITY (DR-33: "por cidade ao vivo"). */
export type InsightPlaceLevel = 'BR' | 'REGION' | 'UF' | 'MUN' | 'ABROAD_CITY' | 'ZONE' | 'POLLING_PLACE' | 'SECTION';

/** O lugar do cartão. Campo que não é do nível não vem (tabela de lugares, §14.5). */
export interface InsightPlace {
  level: InsightPlaceLevel;
  /**
   * REGION: 'N', 'NE', 'CO', 'SE' ou 'S' (nunca 'EX': o Exterior inteiro é UF com 'ZZ'); UF: as 27 UFs ou 'ZZ'; MUN:
   * IBGE de 7 dígitos; ABROAD_CITY: código TSE de 5 dígitos da cidade; ZONE, POLLING_PLACE e SECTION: o IBGE do
   * município, ou o código TSE da cidade com `uf = 'ZZ'`. Ausente em BR.
   */
  code?: string;
  /** UF (ou 'ZZ') do lugar: obrigatório de MUN para baixo; ausente em BR, REGION e UF. */
  uf?: PlaceCode;
  /** ZONE, POLLING_PLACE e SECTION. */
  zone?: number;
  /** POLLING_PLACE (obrigatório) e SECTION (opcional). */
  polling_place?: number;
  /** SECTION: o número da seção aparece na tela (PG-07b). */
  section?: number;
}
export interface InsightTransition {
  /** null = entrada. */
  from: InsightState | null;
  to: InsightState;
  /** Hora do dado, Brasília: mesmo destaque na entrada e no desfecho (DR-33). */
  at: Iso;
  /** seq (ou quadro do replay) em que ocorreu. */
  seq: number;
  /** Fração de seções do lugar naquele instante (0–1). */
  sections_fraction: number;
}
/** O desfecho público de um sinal preliminar, com a hora. */
export interface InsightOutcome {
  type: 'confirmed' | 'dissipated';
  at: Iso;
  seq: number;
}
/** Retirada: o cartão continua na lista, com esta linha e a hora, sem a evidência, e sai da contagem. */
export interface InsightWithdrawal {
  at: Iso;
  reason: 'data_changed' | 'editorial';
}

export const INSIGHT_METRICS = [
  'share',
  'turnout',
  'blank_null',
  'blank',
  'null',
  'profile',
  'tally_timing',
  'transfer',
] as const satisfies readonly InsightMetric[];
export const INSIGHT_LEVELS = ['preliminary', 'alert'] as const satisfies readonly InsightLevel[];
export const INSIGHT_STATES = [
  'preliminary',
  'confirmed',
  'dissipated',
  'alert',
] as const satisfies readonly InsightState[];
export const INSIGHT_PLACE_LEVELS = [
  'BR',
  'REGION',
  'UF',
  'MUN',
  'ABROAD_CITY',
  'ZONE',
  'POLLING_PLACE',
  'SECTION',
] as const satisfies readonly InsightPlaceLevel[];
/** Nível público de cada estado (DR-33): o dissipado continua "sinal preliminar"; o confirmado é "alerta". */
export const INSIGHT_LEVEL_OF: Record<InsightState, InsightLevel> = {
  preliminary: 'preliminary',
  dissipated: 'preliminary',
  confirmed: 'alert',
  alert: 'alert',
};

export const InsightStateSchema = z.enum(INSIGHT_STATES);
/** Só a estrutura: os campos que cada nível exige são invariante (I-26, `insightPlaceProblem`). */
export const InsightPlaceSchema = z.object({
  level: z.enum(INSIGHT_PLACE_LEVELS),
  code: z.optional(z.string()),
  uf: z.optional(PlaceCodeSchema),
  zone: z.optional(IntSchema),
  polling_place: z.optional(IntSchema),
  section: z.optional(IntSchema),
});

/** O prefixo de cada nível na chave do cartão, na ordem de INSIGHT_PLACE_LEVELS. */
const KEY_PREFIXES = ['br', 'reg', 'uf', 'mun', 'abr', 'zon', 'plc', 'sec'];

/**
 * Chave do cartão: um cartão por lugar. O cliente a calcula; o fio não a repete. O nível entra na chave porque 'SE' é
 * UF e região: 'br' · 'reg-ne' · 'uf-sp' · 'uf-zz' · 'mun-3550308' · 'abr-12345' · 'zon-3550308-12' ·
 * 'plc-3550308-12-1040' · 'sec-3550308-12-345'. Só vale para lugar bem formado (`insightPlaceProblem`).
 */
export function insightPlaceKey(place: InsightPlace): string {
  const { level, code, zone } = place;
  const tail = level === 'POLLING_PLACE' ? place.polling_place : level === 'SECTION' ? place.section : undefined;
  const prefix = KEY_PREFIXES[INSIGHT_PLACE_LEVELS.indexOf(level)];
  return [prefix, code?.toLowerCase(), zone, tail].filter((part) => part !== undefined).join('-');
}

const IBGE_RE = /^\d{7}$/;
const TSE_RE = /^\d{5}$/;

/**
 * I-26, a parte da tabela de lugares (§14.5) que vale de BR à cidade: o código e a UF do nível, sem zona, local nem
 * seção. Devolve o campo que foge, ou null. É a função que o validador dos eventos usa (zona, local e seção não geram
 * evento), e por isso mora separada: a parte de baixo da tabela não vai para o JS crítico (B01, §15.7). Com `below`, a
 * chamada é de insightPlaceProblem, para um lugar abaixo de município: valem as regras de código e de UF, e os três
 * campos de baixo ficam com quem chamou.
 */
export function insightAreaProblem(place: InsightPlace, below = false): keyof InsightPlace | null {
  const { level, code, uf } = place;
  const abroad = uf === 'ZZ';
  const listed = (codes: readonly string[]) => code !== undefined && codes.includes(code);
  const codeOk =
    level === 'BR'
      ? code === undefined
      : level === 'REGION'
        ? code !== 'EX' && listed(REGION_CODES)
        : level === 'UF'
          ? listed(PLACE_CODES)
          : code !== undefined && (level === 'ABROAD_CITY' || (below && abroad) ? TSE_RE : IBGE_RE).test(code);
  if (!codeOk) return 'code';
  // MUN nunca em ZZ; cidade do Exterior, só em ZZ
  if (
    level === 'BR' || level === 'REGION' || level === 'UF'
      ? uf !== undefined
      : uf === undefined || (level === 'MUN' ? abroad : level === 'ABROAD_CITY' && !abroad)
  )
    return 'uf';
  if (below) return null;
  for (const field of ['zone', 'polling_place', 'section'] as const) if (place[field] !== undefined) return field;
  return null;
}

/** I-26: o campo do lugar que foge à tabela do nível (§14.5), ou null quando o lugar está bem formado. */
export function insightPlaceProblem(place: InsightPlace): keyof InsightPlace | null {
  const { level, zone, polling_place: pollingPlace, section } = place;
  const below = level === 'ZONE' || level === 'POLLING_PLACE' || level === 'SECTION';
  const area = insightAreaProblem(place, below);
  if (area !== null || !below) return area;
  const positive = (n: number | undefined) => n !== undefined && n >= 1;
  if (!positive(zone)) return 'zone';
  if (
    level === 'POLLING_PLACE'
      ? !positive(pollingPlace)
      : pollingPlace !== undefined && !(level === 'SECTION' && pollingPlace >= 1)
  )
    return 'polling_place';
  if (level === 'SECTION' ? !positive(section) : section !== undefined) return 'section';
  return null;
}

/** Ao vivo, alerta só por cidade (DR-33): município ou cidade do Exterior. Os demais níveis, só no alvo replay. */
export const isLiveInsightLevel = (level: InsightPlaceLevel): boolean => level === 'MUN' || level === 'ABROAD_CITY';

/**
 * A máquina de estados (cartao.py do motor). Sinal: entrada `null → preliminary` ou `null → alert`; desfecho
 * `preliminary → confirmed` ou `preliminary → dissipated`. O cartão tem ainda `preliminary → alert`: os preliminares do
 * lugar se dissiparam e outro quesito saiu da faixa na conclusão. Nenhum estado volta a `preliminary`.
 */
export function insightTransitionAllowed(from: InsightState | null, to: InsightState, card: boolean): boolean {
  if (from === null) return to === 'preliminary' || to === 'alert';
  return from === 'preliminary' && to !== from && (card || to !== 'alert');
}
